import { Divider, Stack, Typography } from '@mui/material';

import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import {
  FormModal,
  FormModalProps,
} from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions/FormModalActions';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
  useSubmitSuccessHandler,
} from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import {
  TenantKind,
  TenantProductTier,
  TenantUsageBucket,
} from '@/types/schema';

import { CreateTenantForm } from '../AdminTenantsPage/CreateTenantModal/CreateTenantForm';
import { CreateTenantFormShape } from '../AdminTenantsPage/CreateTenantModal/CreateTenantForm.types';
import { normalizeEmailDomain } from './AdminOrganizationsPage.utils';
import {
  Operations,
  useAdminOrganizationsPageCreateLuminaryOrganizationMutation,
} from './graphql/AdminOrganizationsPage.generated';

interface CreateOrganizationFormShape extends CreateTenantFormShape {
  organizationDisplayName: string;
  domains: string;
  enableJit: boolean;
}

type CreateOrganizationModalProps = Pick<FormModalProps, 'isOpen' | 'onClose'>;

function parseDomains(value: string) {
  return value.split(',').map(normalizeEmailDomain).filter(Boolean);
}

function CreateOrganizationModalContent({
  isOpen,
  onClose,
}: CreateOrganizationModalProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { formRef, handleSave } = useFormSaveHandler();
  const {
    control,
    handleSubmit,
    reset,
    setError,
    shouldBlockNavigation,
    formState,
  } = useFormContext<CreateOrganizationFormShape>();

  const [createOrganization] =
    useAdminOrganizationsPageCreateLuminaryOrganizationMutation({
      refetchQueries: [Operations.Query.AdminOrganizationsPage],
      awaitRefetchQueries: true,
      onCompleted: () => {
        showFeedback('Organization created', { variant: 'success' });
      },
      onError: (error) => {
        showFeedback(FeedbackMessages.formSaveError);
        reportError('Could not create Luminary organization', error);
      },
    });

  const closeModal = () => {
    reset();
    onClose();
  };

  useSubmitSuccessHandler(closeModal);

  const onSubmit = handleSubmit(async (formData) => {
    const result = await createOrganization({
      variables: {
        input: {
          displayName: formData.organizationDisplayName.trim(),
          domains: parseDomains(formData.domains),
          jitEnabled: formData.enableJit,
          parentTenant: {
            addonAssetProviderIntegrationEnabled:
              formData.addonAssetProviderIntegrationEnabled,
            kind: formData.kind,
            name: formData.name,
            productTier: formData.tier,
            subdomain: formData.subdomain,
            trialEndDate: formData.trialEndDate,
            usageBucket: formData.usageBucket,
          },
        },
      },
    });

    const organizationId =
      result.data?.createLuminaryOrganization.organization.id;
    if (!organizationId) {
      // The mutation registers onError, so Apollo resolves (rather than rejects)
      // with empty data on failure. Set a form error to keep isSubmitSuccessful
      // false so useSubmitSuccessHandler doesn't close and reset the modal, which
      // would discard the user's input. The failure is surfaced via onError.
      // https://github.com/react-hook-form/react-hook-form/issues/2859
      setError('root', {
        type: 'manual',
        message: FeedbackMessages.formSaveError,
      });
      return;
    }
  });

  return (
    <FormModal
      isOpen={isOpen}
      onClose={closeModal}
      heading="Create organization"
      shouldBlockClose={shouldBlockNavigation}
      actions={
        <FormModalActions.Provider formState={formState}>
          <FormModalActions.CancelButton onClick={closeModal} />
          <FormModalActions.SaveButton onClick={handleSave}>
            Create organization
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <Stack spacing={2}>
          <FormAwareTextInput<CreateOrganizationFormShape>
            control={control}
            fieldName="organizationDisplayName"
            label="Organization name"
            placeholder="Acme Wealth"
            required
          />
          <FormAwareTextInput<CreateOrganizationFormShape>
            control={control}
            fieldName="domains"
            label="Initial email domains"
            placeholder="acme.com, acmewealth.com"
            helpText="Optional. Enter domains without the @ symbol and separate multiple domains with commas."
          />
          <FormAwareSwitch<CreateOrganizationFormShape>
            control={control}
            fieldName="enableJit"
            label="Enable customer JIT access"
            labelPosition="right"
            helpText="Just-in-time access applies to the entire organization and all of its tenants."
          />
          <Divider />
          <Typography variant="title-s">Parent tenant</Typography>
          <Typography variant="body-xs">
            The parent tenant is created with the organization and cannot be
            changed later.
          </Typography>
          <CreateTenantForm nameLabel="Parent tenant display name" />
        </Stack>
      </form>
    </FormModal>
  );
}

export function CreateOrganizationModal(props: CreateOrganizationModalProps) {
  const formMethods = useForm<CreateOrganizationFormShape>({
    defaultValues: {
      organizationDisplayName: '',
      domains: '',
      enableJit: false,
      name: '',
      subdomain: '',
      addonAssetProviderIntegrationEnabled: false,
      tier: TenantProductTier.Pro,
      trialEndDate: null,
      kind: '' as TenantKind,
      usageBucket: '' as TenantUsageBucket,
    },
  });

  return (
    <FormProvider {...formMethods}>
      <CreateOrganizationModalContent {...props} />
    </FormProvider>
  );
}
